import {isAndroid} from "FirebaseUtils";

export type ColorScheme = "light" | "dark";
export type ColorSchemePreference = ColorScheme | "system";

const STORAGE_KEY = "ws-color-scheme";
const THEME_COLORS: Record<ColorScheme, string> = {light: "#ffffff", dark: "#0f1517"};

type Listener = (scheme: ColorScheme) => void;
const listeners = new Set<Listener>();

let preference: ColorSchemePreference = readStoredPreference();
let urlOverride: ColorScheme | null = null;
let systemDark = false;
let current: ColorScheme = "light";

function readStoredPreference(): ColorSchemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch (_) {
    // storage blocked: fall back to the system setting
  }
  return "system";
}

/**
 * The native apps pass the theme on the URL (`?darkMode=true|false` or `?theme=dark|light`).
 * @param {string} search - a location search string
 * @return {ColorScheme | null} the scheme the URL asks for, if any
 */
export function parseColorSchemeParam(search: string): ColorScheme | null {
  const params = new URLSearchParams(search || "");
  const param = params.get("darkMode") || params.get("theme");
  if (param === "true" || param === "dark") return "dark";
  if (param === "false" || param === "light") return "light";
  return null;
}

function resolve(): ColorScheme {
  // Android's native chrome is light only; the WebView follows it.
  if (isAndroid) return "light";
  if (urlOverride) return urlOverride;
  if (preference === "system") return systemDark ? "dark" : "light";
  return preference;
}

/**
 * The MapKit colour scheme matching the active theme, for `new mapkit.Map(id, {colorScheme})`.
 * @return {string | undefined} a mapkit.Map.ColorSchemes value, or undefined before MapKit loads
 */
export function mapkitColorScheme(): string | undefined {
  const schemes = (window as any).mapkit?.Map?.ColorSchemes;
  if (!schemes) return undefined;
  return document.documentElement.getAttribute("data-theme") === "dark" ? schemes.Dark : schemes.Light;
}

function syncMapkitMaps() {
  const scheme = mapkitColorScheme();
  if (!scheme) return;
  ((window as any).mapkit?.maps || []).forEach((map: any) => {
    try {
      map.colorScheme = scheme;
    } catch (_) {
      // a map being torn down can refuse the change
    }
  });
}

function apply() {
  const next = resolve();
  const root = document.documentElement;
  root.setAttribute("data-theme", next);
  root.style.colorScheme = next;
  document.querySelector("meta[name=theme-color]")?.setAttribute("content", THEME_COLORS[next]);
  if (next !== current) {
    current = next;
    syncMapkitMaps();
    listeners.forEach((listener) => listener(next));
  }
}

const ColorSchemeService = {
  init(search: string = window.location.search) {
    urlOverride = parseColorSchemeParam(search);
    if (window.matchMedia) {
      const query = window.matchMedia("(prefers-color-scheme: dark)");
      systemDark = query.matches;
      const onChange = (event: MediaQueryListEvent) => {
        systemDark = event.matches;
        apply();
      };
      if (query.addEventListener) query.addEventListener("change", onChange);
      else query.addListener(onChange);
    }
    apply();
  },
  setUrlOverride(search: string) {
    const fromUrl = parseColorSchemeParam(search);
    if (fromUrl) {
      urlOverride = fromUrl;
      apply();
    }
  },
  getPreference(): ColorSchemePreference {
    return preference;
  },
  setPreference(next: ColorSchemePreference) {
    preference = next;
    urlOverride = null;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      // storage blocked: the choice lasts for this session only
    }
    apply();
  },
  getColorScheme(): ColorScheme {
    return current;
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
};

export default ColorSchemeService;
